// ImageMagnifier.js
import { useState, useEffect } from 'react';

const ImageMagnifier = ({
    src,
    className,
    width,
    height,
    alt,
    magnifierHeight = 150,
    magnifierWidth = 150,
    zoomLevel = 3
}) => {
    const [showMagnifier, setShowMagnifier] = useState(false);
    const [[imgWidth, imgHeight], setSize] = useState([0, 0]);
    const [[x, y], setXY] = useState([0, 0]);
    const [isMobile, setIsMobile] = useState(false);
    const [touchActive, setTouchActive] = useState(false);

    // Check if device is mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);

    const handleImageLoad = (e) => {
        const el = e.currentTarget;
        const { width, height } = el.getBoundingClientRect();
        setSize([width, height]);
    };

    const mouseEnter = (e) => {
        if (isMobile) return;
        
        const el = e.currentTarget;
        const { width, height } = el.getBoundingClientRect();
        setSize([width, height]);
        setShowMagnifier(true);
    };

    const mouseLeave = (e) => {
        if (isMobile) return;
        
        e.preventDefault();
        setShowMagnifier(false);
    };

    const mouseMove = (e) => {
        if (isMobile) return;
        
        const el = e.currentTarget;
        const { top, left } = el.getBoundingClientRect();

        const x = e.pageX - left - window.scrollX;
        const y = e.pageY - top - window.scrollY;

        setXY([x, y]);
    };

    // Touch event handlers for mobile
    const handleTouchStart = (e) => {
        if (!isMobile) return;
        
        const el = e.currentTarget;
        const { width, height } = el.getBoundingClientRect();
        setSize([width, height]);
        setTouchActive(true);
        
        const touch = e.touches[0];
        const { top, left } = el.getBoundingClientRect();
        
        const x = touch.pageX - left - window.scrollX;
        const y = touch.pageY - top - window.scrollY;
        
        setXY([x, y]);
        setShowMagnifier(true);
    };

    const handleTouchMove = (e) => {
        if (!isMobile || !touchActive) return;
        
        e.preventDefault(); // Prevent scrolling while magnifying
        
        const el = e.currentTarget;
        const { top, left } = el.getBoundingClientRect();
        
        const touch = e.touches[0];
        const x = touch.pageX - left - window.scrollX;
        const y = touch.pageY - top - window.scrollY;
        
        setXY([x, y]);
    };

    const handleTouchEnd = () => {
        if (!isMobile) return;
        
        setTouchActive(false);
        setShowMagnifier(false);
    };

    return (
        <div className="relative inline-block w-full h-full">
            <img
                src={src}
                className={`${className} ${showMagnifier ? 'opacity-90' : 'opacity-100'}`}
                width={width}
                height={height}
                alt={alt}
                onLoad={handleImageLoad}
                onMouseEnter={mouseEnter}
                onMouseLeave={mouseLeave}
                onMouseMove={mouseMove}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
            />
            {showMagnifier && (
                <div
                    className="shadow-md"
                    style={{
                        position: 'absolute',
                        pointerEvents: 'none',
                        height: `${magnifierHeight}px`,
                        width: `${magnifierWidth}px`,
                        opacity: '1',
                        border: '1px solid #e0e0e0',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        backgroundImage: `url('${src}')`,
                        backgroundRepeat: 'no-repeat',
                        top: `${y - magnifierHeight / 2}px`,
                        left: `${x - magnifierWidth / 2}px`,
                        backgroundSize: `${imgWidth * zoomLevel}px ${imgHeight * zoomLevel}px`,
                        backgroundPositionX: `${-x * zoomLevel + magnifierWidth / 2}px`,
                        backgroundPositionY: `${-y * zoomLevel + magnifierHeight / 2}px`,
                        zIndex: 10
                    }}
                />
            )}
           
        </div>
    );
};

export default ImageMagnifier;